import { SxProps, Theme } from "@mui/material";
import { Typography, TypographyProps } from "src/components-mui/base/typography/typography";
import { toSxArray } from "src/utils/mui/sx/sx";

const headingFontSizeMap = {
    "6xl": {
        fontSize: "6xl",
        fontWeight: "heavy",
        letterSpacing: "1.5px",
        lineHeight: 1.15,
    },
    "5xl": {
        fontSize: "5xl",
        fontWeight: "heavy",
        letterSpacing: "1.5px",
        lineHeight: 1.15,
    },
    "4xl": {
        fontSize: "4xl",
        fontWeight: "heavy",
        letterSpacing: "0",
        lineHeight: 1.25,
    },
    "3xl": {
        fontSize: "3xl",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.25,
    },
    "2xl": {
        fontSize: "2xl",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.5,
    },
    xl: {
        fontSize: "xl",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.6,
    },
    lg: {
        fontSize: "lg",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.5,
    },
    md: {
        fontSize: "md",
        fontWeight: "bold",
        letterSpacing: "0",
        lineHeight: 1.5,
    },
    sm: {
        fontSize: "sm",
        fontWeight: "semibold",
        letterSpacing: "0",
        lineHeight: 1.5,
    },
};

export type IFontSize = keyof typeof headingFontSizeMap;

export interface IFontSizeResponsiveMap {
    xs?: IFontSize;
    sm?: IFontSize;
    md?: IFontSize;
    lg?: IFontSize;
}

type HeadingToken = (typeof headingFontSizeMap)[IFontSize];

export const buildResponsiveHeaderFont = (fontSize: IFontSize | IFontSizeResponsiveMap): SxProps<Theme> => {
    if (typeof fontSize === "string") {
        return headingFontSizeMap[fontSize];
    }

    // MUI's sx prop natively turns `{ xs, sm, md, lg }` on any style property into media queries.
    // We only need to transpose the per-breakpoint token lookup into per-property responsive maps.
    const responsive: { [K in keyof HeadingToken]: Record<string, HeadingToken[K]> } = {
        fontSize: {},
        fontWeight: {},
        letterSpacing: {},
        lineHeight: {},
    };

    const entries = Object.entries(fontSize) as Array<[keyof IFontSizeResponsiveMap, IFontSize | undefined]>;

    for (const [breakpoint, size] of entries) {
        if (!size) {
            continue;
        }
        const token = headingFontSizeMap[size];
        responsive.fontSize[breakpoint] = token.fontSize;
        responsive.fontWeight[breakpoint] = token.fontWeight;
        responsive.letterSpacing[breakpoint] = token.letterSpacing;
        responsive.lineHeight[breakpoint] = token.lineHeight;
    }

    return responsive;
};

export interface IWfHeadingProps extends Omit<TypographyProps, "fontSize"> {
    fontSize: IFontSize | IFontSizeResponsiveMap;
}

export const WfHeading = ({ fontSize, sx, ...props }: IWfHeadingProps) => (
    <Typography {...props} sx={[...toSxArray(buildResponsiveHeaderFont(fontSize)), ...toSxArray(sx)]} />
);
